import React, { useLayoutEffect, useRef, useState } from "react";
import ReactApexChart from "react-apexcharts";
import NoData, { hasData } from "./NoData";
import { BRAND, NEUTRAL, getCategoryColor } from "../../utils/chartTheme";

export const CHART_HEIGHT = 320;

const LABEL_CLASS = "dashboard-bar-label";
const SVG_NS = "http://www.w3.org/2000/svg";
const ANIMATION_MS = 800;

const MIN_VISIBLE_SHARE = 0.005;

const formatCompact = (num) => {
  const n = Number(num) || 0;
  if (Math.abs(n) >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return Math.round(n).toLocaleString();
};

const formatCount = (num) => (Number(num) || 0).toLocaleString();

const toPercent = (part, total) =>
  total > 0 ? ((part / total) * 100).toFixed(1) : "0.0";

const wrapWords = (text, maxChars) => {
  const lines = [];
  let line = "";
  text.split(/\s+/).filter(Boolean).forEach((word) => {
    if (line && (line + " " + word).length > maxChars) {
      lines.push(line);
      line = word;
    } else {
      line = line ? `${line} ${word}` : word;
    }
  });
  if (line) lines.push(line);
  return lines;
};

const wrapLabel = (label, maxChars) => {
  const text = String(label ?? "");

  const hasSlash = text.includes("/") && text.length > 8;
  if (!hasSlash && text.length <= maxChars) return text;
  const parts = text.split("/").map((p, i) => (i ? `/${p.trim()}` : p.trim()));
  const lines = parts.flatMap((part) => wrapWords(part, maxChars));
  return lines.length > 1 ? lines : lines[0] || text;
};

const svgEl = (tag, attrs) => {
  const el = document.createElementNS(SVG_NS, tag);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  el.setAttribute("class", LABEL_CLASS);
  el.setAttribute("pointer-events", "none");
  return el;
};

const textColorFor = (fill) => {
  const value = String(fill || "").trim();
  const hex = /^#([0-9a-f]{6})$/i.exec(value);
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(value);
  let r;
  let g;
  let b;
  if (hex) {
    const n = parseInt(hex[1], 16);
    [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  } else if (rgb) {
    [r, g, b] = [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  } else {
    return "#ffffff";
  }
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? "#1F2937" : "#ffffff";
};

const fittingLevel = (layer, box, candidates) => {
  if (!candidates.length || box.height < 16 || box.width < 16) return -1;
  const text = svgEl("text", { "font-size": "12px", "font-weight": "bold" });
  layer.appendChild(text);
  const level = candidates.findIndex((label) => {
    text.textContent = label;
    const drawn = text.getBBox();
    return drawn.width > 0 && drawn.width <= box.width - 12 && drawn.height <= box.height;
  });
  text.remove();
  return level;
};

const addInsideLabel = (layer, box, candidates, barFill, minLevel = 0) => {
  if (!candidates.length || box.height < 16 || box.width < 16) return;
  const text = svgEl("text", {
    x: box.x + box.width / 2,
    y: box.y + box.height / 2,
    "text-anchor": "middle",
    "dominant-baseline": "central",
    fill: textColorFor(barFill),
    "font-size": "12px",
    "font-weight": "bold",
  });
  layer.appendChild(text);
  const fit = candidates.slice(Math.min(minLevel, candidates.length - 1)).find((label) => {
    text.textContent = label;
    const drawn = text.getBBox();
    return drawn.width > 0 && drawn.width <= box.width - 12 && drawn.height <= box.height;
  });
  if (fit) text.textContent = fit;
  else text.remove();
};

const addBadge = (layer, x, y, label, horizontal, small = false) => {
  const text = svgEl("text", {
    x,
    y,
    "text-anchor": horizontal ? "start" : "middle",
    "dominant-baseline": "central",
    fill: "#374151",
    "font-size": small ? "9px" : "10px",
    "font-weight": "bold",
  });
  text.textContent = label;
  layer.appendChild(text);
  const box = text.getBBox();
  const padX = small ? 3 : 4;
  const rect = svgEl("rect", {
    x: box.x - padX,
    y: box.y - 2,
    width: box.width + padX * 2,
    height: box.height + 4,
    rx: 3,
    fill: "#ffffff",
    stroke: NEUTRAL.border,
  });
  layer.insertBefore(rect, text);
  return {
    bounds: () => {
      const r = rect.getBBox();
      return { x: r.x, y: r.y, w: r.width, h: r.height };
    },
    remove: () => {
      text.remove();
      rect.remove();
    },
  };
};

const overlaps = (a, b) =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

const drawLabels = (chart, { getCandidates, getBadge, horizontal }) => {
  if (!chart?.el || !chart.el.isConnected) return;
  chart.el.querySelectorAll(`.${LABEL_CLASS}`).forEach((el) => el.remove());
  const bars = Array.from(chart.el.querySelectorAll("path.apexcharts-bar-area"));
  if (!bars.length) return;
  const ends = {}; // outer end of each category's bar (or stack), keyed by `${series}:${point}` or point

  const minLevel = bars.reduce((level, bar) => {
    const box = bar.getBBox();
    const candidates = getCandidates(Number(bar.getAttribute("index")), Number(bar.getAttribute("j")));
    const fits = fittingLevel(bar.parentNode, box, candidates);
    return fits > level ? fits : level;
  }, 0);

  bars.forEach((bar) => {
    const i = Number(bar.getAttribute("index"));
    const j = Number(bar.getAttribute("j"));
    const box = bar.getBBox();
    if (!box.width && !box.height) return;
    addInsideLabel(bar.parentNode, box, getCandidates(i, j), bar.getAttribute("fill"), minLevel);
    const key = getBadge.perSeries ? `${i}:${j}` : `${j}`;
    const end = horizontal ? box.x + box.width : box.y;
    const prev = ends[key];
    if (!prev || (horizontal ? end > prev.end : end < prev.end)) {
      ends[key] = { end, box, i, j, layer: bar.parentNode };
    }
  });

  const placed = [];
  Object.values(ends)
    .sort((a, b) =>
      horizontal ? b.box.width - a.box.width : b.box.height - a.box.height
    )
    .forEach(({ end, box, i, j, layer }) => {
      const label = getBadge(i, j);
      if (!label) return;
      const badge = horizontal
        ? addBadge(layer, end + 8, box.y + box.height / 2, label, true)
        : addBadge(layer, box.x + box.width / 2, end - 12, label, false, getBadge.perSeries);

      const bounds = badge.bounds();
      if (placed.some((p) => overlaps(p, bounds))) badge.remove();
      else placed.push(bounds);
    });
};

const InsideValue = ({ candidates }) => {
  const boxRef = useRef(null);
  const measureRef = useRef(null);
  const [shown, setShown] = useState("");
  const candidatesRef = useRef(candidates);
  candidatesRef.current = candidates;
  const key = candidates.join("|");
  useLayoutEffect(() => {
    const box = boxRef.current;
    const measure = measureRef.current;
    if (!box || !measure) return undefined;
    const check = () => {
      const room = box.clientWidth - 10;
      const fit = candidatesRef.current.find((label) => {
        measure.textContent = label;
        return measure.offsetWidth <= room;
      });
      setShown(fit || "");
    };
    check();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(check);
    observer.observe(box);
    return () => observer.disconnect();
  }, [key]);
  return (

    <span ref={boxRef} className="absolute inset-0 flex items-center justify-center">
      <span className="text-white text-xs font-bold whitespace-nowrap">{shown}</span>
      <span ref={measureRef} aria-hidden="true" className="absolute invisible text-xs font-bold whitespace-nowrap left-0 top-0 h-0 overflow-hidden" />
    </span>
  );
};

const BarList = ({ rows, height, getColor, getPercent, candidatesFor, tooltipFor }) => {
  const largest = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="flex flex-col justify-center gap-2 py-2" style={{ minHeight: height }}>
      {rows.map((r, j) => {
        const pct = getPercent(j);
        const visible = r.count > 0 && pct !== "0.0";
        const share = visible ? Math.max(r.count / largest, MIN_VISIBLE_SHARE) : 0;
        return (
          <div key={r.name} title={tooltipFor(j)}>
            <div className="text-[11px] font-semibold text-gray-600 leading-tight mb-1">{r.name}</div>
            <div className="flex items-center gap-1.5 h-5">
              {visible && (
                <div
                  className="relative h-full rounded-r-md transition-all duration-700"
                  style={{
                    width: `calc((100% - 56px) * ${share})`,
                    backgroundColor: getColor(j),
                  }}
                >
                  <InsideValue candidates={candidatesFor(j)} />
                </div>
              )}
              <span className="text-[10px] font-bold text-gray-700 bg-white border border-gray-200 rounded px-1 py-0.5 whitespace-nowrap">
                {visible ? `${pct}%` : "0%"}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const DashboardBarChart = ({
  items,
  categories: categoriesProp,
  series: seriesProp,
  horizontal: horizontalProp = false,
  vertical = false,
  allowList = true,
  tilt = false,
  fold = true,
  segmentLabel = "both",
  stacked,
  unit = "",
  prefix = "",
  height,
}) => {

  const boxRef = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    const measure = () => setWidth(box.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  });

  const formatAxis = (val) => `${prefix}${formatCompact(val)}`;
  const formatFull = (val) => `${prefix}${formatCount(val)}`;

  const rawCategories = items ? items.map((item) => item.name) : categoriesProp || [];
  const rawSeries = items
    ? [{ name: "Count", data: items.map((item) => Number(item.count) || 0) }]
    : (seriesProp || []).map((s) => ({
        ...s,
        data: (s.data || []).map((v) => Number(v) || 0),
      }));

  if (!rawCategories.length || !hasData(rawSeries.map((s) => s.data))) {
    return <NoData />;
  }

  const single = rawSeries.length === 1;
  const isStacked = !single && (stacked ?? true);
  const grouped = !single && !isStacked;
  const legendRoom = single ? 0 : 40;

  const slot = width ? (width - 80) / rawCategories.length : 0;
  const charsPerLine = slot ? Math.floor(slot / 7.2) : 10;
  const labelsFit =
    rawCategories.length <= 8 &&
    rawCategories.every((c) => String(c ?? "").length <= charsPerLine);

  const longestName = Math.max(
    ...rawCategories.flatMap((c) => String(c ?? "").split("/").map((p) => p.trim().length))
  );
  const tooLongForColumns = longestName > 30;
  const horizontal = vertical ? tooLongForColumns : horizontalProp || !labelsFit;

  const labelWidth = Math.round(Math.min(240, Math.max(90, (width || 300) * 0.38)));
  const labelChars = Math.max(8, Math.floor(labelWidth / 7.2));
  const namesFitColumn = rawCategories.every((c) => {
    const text = String(c ?? "");
    const longestWord = Math.max(...text.split(/\s+/).map((w) => w.length));
    return longestWord <= labelChars && [].concat(wrapLabel(text, labelChars)).length <= 2;
  });
  const useList = allowList && !vertical && single && horizontal && !namesFitColumn;
  const listChars = Math.max(10, Math.floor((width || 300) / 7));
  const rowNeed = (name) =>
    useList
      ? [].concat(wrapLabel(name, listChars)).length * 14 + 28
      : [].concat(wrapLabel(name, labelChars)).length * 14 + 12;
  const baseHeight = height ?? CHART_HEIGHT + legendRoom;
  const plotRoom = useList ? baseHeight - 16 : baseHeight - 70 - legendRoom;
  const fits = (names) => names.reduce((sum, n) => sum + rowNeed(n), 0) <= plotRoom;

  let rows = single
    ? rawCategories.map((name, j) => ({
        name,
        count: rawSeries[0].data[j],
        percentage: items?.[j]?.percentage,
        color: items?.[j]?.color,
        details: items?.[j]?.details,
      }))
    : null;
  if (useList) rows = rows.filter((r) => r.count > 0);
  let otherDetails = [];
  if (fold && single && horizontal && rows.length > 1 && !fits(rows.map((r) => r.name))) {
    const total = rows.reduce((sum, r) => sum + r.count, 0);
    const byValue = [...rows].sort((a, b) => b.count - a.count);
    let keep = byValue.length - 1;
    const build = (k) => {
      const kept = new Set(byValue.slice(0, k));
      const rest = rows.filter((r) => !kept.has(r));
      const restCount = rest.reduce((sum, r) => sum + r.count, 0);
      const restPct = rest.every((r) => r.percentage !== undefined && r.percentage !== null)
        ? rest.reduce((sum, r) => sum + (parseFloat(r.percentage) || 0), 0).toFixed(1)
        : toPercent(restCount, total);
      return {
        rest,
        rows: [
          ...rows.filter((r) => kept.has(r)),
          { name: `Other (${rest.length})`, count: restCount, percentage: restPct },
        ],
      };
    };
    let result = build(keep);
    while (keep > 1 && !fits(result.rows.map((r) => r.name))) {
      keep -= 1;
      result = build(keep);
    }
    rows = result.rows;
    otherDetails = result.rest.map((r) => `${r.name}: ${formatFull(r.count)}`);
  }

  const categories = single ? rows.map((r) => r.name) : rawCategories;
  const series = single ? [{ name: "Count", data: rows.map((r) => r.count) }] : rawSeries;
  const otherIndex = otherDetails.length ? categories.length - 1 : -1;

  const chartHeight =
    (!single || !fold) && horizontal && height === undefined
      ? Math.max(baseHeight, categories.reduce((s, n) => s + rowNeed(n), 0) + 50 + legendRoom)
      : baseHeight;

  const singleTotal = single ? series[0].data.reduce((a, b) => a + b, 0) : 0;
  const singlePercent = (j) => rows?.[j]?.percentage ?? toPercent(series[0].data[j], singleTotal);
  const categoryTotal = (j) => series.reduce((sum, s) => sum + (s.data[j] || 0), 0);

  const largest = Math.max(
    ...categories.map((_, j) =>
      isStacked ? categoryTotal(j) : Math.max(...series.map((s) => s.data[j] || 0))
    )
  );
  const minVisible = largest * MIN_VISIBLE_SHARE;
  const shareOf = (v, j) => toPercent(v, isStacked ? categoryTotal(j) : single ? singleTotal : largest);
  const displaySeries = series.map((s) => ({
    name: s.name,
    data: s.data.map((v, j) => (v > 0 && shareOf(v, j) !== "0.0" ? Math.max(v, minVisible) : 0)),
  }));

  const perBarColors = single && rows.some((r) => r.color);
  const colors = perBarColors
    ? rows.map((r) => r.color || BRAND.teal)
    : single
    ? [rawSeries[0].color || BRAND.teal]
    : series.map((s, i) => s.color || getCategoryColor(s.name, i));

  const valueCandidates = (val) =>
    [formatAxis(val), formatFull(val)].filter((v, k, all) => all.indexOf(v) === k);

  const getCandidates = (i, j) => {
    const val = series[i]?.data[j];
    if (!val || grouped) return [];
    if (single) return valueCandidates(val);
    const share = toPercent(val, categoryTotal(j));
    if (segmentLabel === "share") return [`${share}%`];
    if (segmentLabel === "count") return valueCandidates(val);
    return [
      ...valueCandidates(val).map((v) => `${v} (${share}%)`),
      ...valueCandidates(val),
    ];
  };
  const getBadge = (i, j) => {
    if (single) return series[0].data[j] && singlePercent(j) !== "0.0" ? `${singlePercent(j)}%` : "";
    if (isStacked) return categoryTotal(j) ? formatAxis(categoryTotal(j)) : "";
    return series[i].data[j] ? formatAxis(series[i].data[j]) : "";
  };
  getBadge.perSeries = grouped;

  if (useList) {
    return (
      <div ref={boxRef} className="w-full">
        <BarList
          rows={rows}
          height={chartHeight}
          getColor={(j) => colors[perBarColors ? j : 0]}
          getPercent={(j) => String(singlePercent(j))}
          candidatesFor={(j) => valueCandidates(rows[j].count)}
          tooltipFor={(j) =>
            [
              `${rows[j].name}: ${formatFull(rows[j].count)}${unit ? ` ${unit}` : ""} (${singlePercent(j)}%)`,
              ...(j === otherIndex ? otherDetails : rows[j].details || []),
            ].join("\n")
          }
        />
      </div>
    );
  }

  const scheduleLabels = (chart) =>
    setTimeout(
      () => drawLabels(chart, { getCandidates, getBadge, horizontal }),
      ANIMATION_MS + 100
    );

  const tiltLabels =
    (tilt && !horizontal) ||
    (!horizontal &&
    categories.some((c) => {
      const text = String(c ?? "");
      const longestWord = Math.max(...text.split(/[\s/]+/).map((w) => w.length));
      return longestWord > charsPerLine || [].concat(wrapLabel(text, charsPerLine)).length > 3;
    }));

  const tiltRoom = Math.min(90, Math.max(...categories.map((c) => String(c ?? "").length)) * 4);

  const valueAxisLabels = {
    style: { fontSize: "10px", colors: NEUTRAL.muted },
    formatter: (val) => formatAxis(val),
  };
  const categoryAxisLabels = {
    style: { fontSize: "11px", fontWeight: 600, colors: NEUTRAL.muted },
    trim: false,
    hideOverlappingLabels: false,
  };

  const tooltipTitle = (j) => {
    const details = j === otherIndex ? otherDetails : rows?.[j]?.details || [];
    if (!details.length) return categories[j] ?? "";
    const shown = details.slice(0, 8).join("<br/>");
    const more = details.length > 8 ? `<br/>+${details.length - 8} more` : "";
    return `${categories[j]}<div style="font-weight:400;font-size:11px;margin-top:4px">${shown}${more}</div>`;
  };

  const options = {
    chart: {
      type: "bar",
      stacked: isStacked,
      toolbar: { show: false },
      animations: { enabled: true, easing: "easeinout", speed: ANIMATION_MS },
      events: { mounted: scheduleLabels, updated: scheduleLabels },
    },
    plotOptions: {
      bar: {
        horizontal,
        distributed: perBarColors,
        borderRadius: 6,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
        ...(horizontal ? { barHeight: "65%" } : { columnWidth: grouped ? "80%" : "55%" }),
      },
    },
    colors,
    dataLabels: { enabled: false },
    stroke: { show: true, width: 1, colors: ["#fff"] },
    xaxis: {
      categories:
        horizontal || tiltLabels ? categories : categories.map((c) => wrapLabel(c, charsPerLine)),
      labels: horizontal
        ? valueAxisLabels
        : tiltLabels
        ? { ...categoryAxisLabels, rotate: -45, rotateAlways: true, maxHeight: 130 }
        : { ...categoryAxisLabels, rotate: 0, rotateAlways: false },
    },
    yaxis: {
      labels: horizontal
        ? {
            ...categoryAxisLabels,
   
            minWidth: labelWidth,
            maxWidth: labelWidth,
            formatter: (val) => wrapLabel(val, labelChars),
          }
        : valueAxisLabels,
    },
    grid: {
      borderColor: NEUTRAL.grid,
      strokeDashArray: 4,
      xaxis: { lines: { show: horizontal } },
      yaxis: { lines: { show: !horizontal } },
      padding: horizontal
        ? { top: 0, right: 60, bottom: 0, left: 10 }
        : { top: 24, right: 10, bottom: 0, left: tiltLabels ? tiltRoom : 10 },
    },
    legend: {
      show: !single,
      position: "top",
      horizontalAlign: "center",
      fontSize: "12px",
      markers: { size: 6 },
    },
    states: { hover: { filter: { type: "darken", value: 0.9 } } },
    tooltip: {
      theme: "light",

      followCursor: true,
      shared: true,
      intersect: false,
      x: { formatter: (_, opts) => tooltipTitle(opts.dataPointIndex) },
      y: {
        title: { formatter: (name) => (single ? "" : `${name}:`) },
        formatter: (_, opts) => {
          const i = opts.seriesIndex;
          const j = opts.dataPointIndex;
          const val = series[i]?.data[j] || 0;
          const share = single ? singlePercent(j) : toPercent(val, categoryTotal(j));
          return `${formatFull(val)}${unit ? ` ${unit}` : ""} (${share}%)`;
        },
      },
    },
  };

  return (
    <div ref={boxRef} className="w-full">
      <ReactApexChart
        key={`${horizontal ? "h" : "v"}-${categories.length}`}
        options={options}
        series={displaySeries}
        type="bar"
        height={chartHeight}
      />
    </div>
  );
};

export default DashboardBarChart;
